import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Search, X } from "lucide-react";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/**
 * Two small comboboxes — a single-select replacement for the native
 * `<select>` and a multi-select with checkboxes. Both have an inline
 * search input that filters the option list in real time.
 *
 * Why not the shadcn Command primitive? It depends on `cmdk` +
 * `@radix-ui/react-popover`, neither of which is installed. These
 * components are ~150 LOC of plain React using only what's already
 * in the bundle (Input + lucide-react icons).
 *
 * Pattern: render an inline trigger that mirrors the look of the
 * existing input fields. Click → drop a panel below it (absolute
 * positioning, parent must be relative). Outside-click / Escape /
 * tab-out closes the panel.
 */

export interface SelectOption<V extends string | number = number> {
  value: V;
  label: string;
  /** Optional secondary text under the label (e.g. "led by X"). */
  hint?: string;
  /** Visually mute the row and prevent selection. */
  disabled?: boolean;
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function useOutsideClose(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown",   onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown",   onKey);
    };
  }, [open, onClose]);
  return ref;
}

function filterOptions<V extends string | number>(
  options: SelectOption<V>[], query: string,
): SelectOption<V>[] {
  const q = query.trim().toLowerCase();
  if (!q) return options;
  return options.filter((o) =>
    o.label.toLowerCase().includes(q) ||
    (o.hint?.toLowerCase().includes(q) ?? false),
  );
}

// ---------------------------------------------------------------------------
// Single-select
// ---------------------------------------------------------------------------

interface SearchableSelectProps<V extends string | number> {
  value: V | null;
  onChange: (next: V | null) => void;
  options: SelectOption<V>[];
  placeholder?: string;
  /** Text shown above the option list (when no matches) — e.g.
   *  "No directors in your department yet." */
  emptyHint?: string;
  disabled?: boolean;
  /** Allow clearing the selection back to null. */
  clearable?: boolean;
  id?: string;
}

export function SearchableSelect<V extends string | number = number>({
  value, onChange, options, placeholder = "Select…",
  emptyHint, disabled, clearable = true, id,
}: SearchableSelectProps<V>) {
  const [open,  setOpen]  = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useOutsideClose(open, () => setOpen(false));

  const filtered = useMemo(() => filterOptions(options, query), [options, query]);
  const selected = options.find((o) => o.value === value) ?? null;

  function pick(opt: SelectOption<V>) {
    if (opt.disabled) return;
    onChange(opt.value);
    setOpen(false);
    setQuery("");
  }

  return (
    <div ref={wrapRef} className="relative">
      <button
        id={id}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "flex h-10 w-full items-center gap-2 rounded-md border border-input bg-background px-3 text-sm font-medium",
          "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50",
          !selected && "text-muted-foreground",
        )}
      >
        <span className="flex-1 truncate text-left">
          {selected ? selected.label : placeholder}
        </span>
        {clearable && selected && !disabled && (
          <span
            role="button" tabIndex={-1} aria-label="Clear selection"
            onClick={(e) => { e.stopPropagation(); onChange(null); }}
            className="rounded-sm p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </span>
        )}
        <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-md border border-input bg-popover shadow-md">
          <SearchBox value={query} onChange={setQuery} />
          <ul className="max-h-56 overflow-y-auto p-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-6 text-center text-xs text-muted-foreground">
                {emptyHint ?? "No matches."}
              </li>
            ) : (
              filtered.map((opt) => {
                const isSel = opt.value === value;
                return (
                  <li key={String(opt.value)}>
                    <button
                      type="button"
                      onClick={() => pick(opt)}
                      disabled={opt.disabled}
                      className={cn(
                        "flex w-full items-start gap-2 rounded px-2 py-1.5 text-left text-sm",
                        opt.disabled
                          ? "cursor-not-allowed opacity-50"
                          : "hover:bg-accent hover:text-accent-foreground",
                        isSel && "bg-primary/10 text-primary",
                      )}
                    >
                      <Check className={cn("mt-0.5 h-3.5 w-3.5 shrink-0", isSel ? "opacity-100" : "opacity-0")} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate">{opt.label}</span>
                        {opt.hint && (
                          <span className="block truncate text-xs text-muted-foreground">{opt.hint}</span>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Multi-select
// ---------------------------------------------------------------------------

interface SearchableMultiSelectProps<V extends string | number> {
  values: V[];
  onChange: (next: V[]) => void;
  options: SelectOption<V>[];
  emptyHint?: string;
  /** Optional max height for the option list (Tailwind class). */
  maxHeightClass?: string;
}

/**
 * Inline multi-select with a search input above a scrollable
 * checkbox list. No popover trigger — the list is always visible,
 * which suits the dept-pickers in the user form (you want to see
 * the current selection at a glance).
 */
export function SearchableMultiSelect<V extends string | number = number>({
  values, onChange, options, emptyHint, maxHeightClass = "max-h-48",
}: SearchableMultiSelectProps<V>) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => filterOptions(options, query), [options, query]);
  const valueSet = useMemo(() => new Set(values), [values]);

  function toggle(opt: SelectOption<V>, checked: boolean) {
    if (opt.disabled) return;
    if (checked) onChange([...values, opt.value]);
    else         onChange(values.filter((v) => v !== opt.value));
  }

  return (
    <div className="overflow-hidden rounded-md border bg-background">
      <SearchBox value={query} onChange={setQuery} />
      <div className={cn("space-y-0.5 overflow-y-auto p-1", maxHeightClass)}>
        {filtered.length === 0 ? (
          <p className="px-3 py-4 text-center text-xs text-muted-foreground">
            {emptyHint ?? "No matches."}
          </p>
        ) : (
          filtered.map((opt) => {
            const checked = valueSet.has(opt.value);
            return (
              <label
                key={String(opt.value)}
                className={cn(
                  "flex cursor-pointer items-start gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent/40",
                  opt.disabled && "cursor-not-allowed opacity-50",
                )}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={opt.disabled}
                  onChange={(e) => toggle(opt, e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-primary"
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{opt.label}</div>
                  {opt.hint && (
                    <div className="truncate text-xs text-muted-foreground">{opt.hint}</div>
                  )}
                </div>
              </label>
            );
          })
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Internal: the search row that sits at the top of both pickers.
// ---------------------------------------------------------------------------

function SearchBox({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="relative border-b border-border/60 bg-muted/30 p-2">
      <Search className="pointer-events-none absolute left-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
      <Input
        type="search" autoFocus
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search…"
        className="h-8 pl-8 pr-2 text-sm"
      />
    </div>
  );
}
